import type { DocumentBlock, DocumentKind, PlanDocument, Project, TaskActivity } from "@/engine/types";
import { newId } from "@/store/tree-ops";
import { isBlankBlocks, plainTextFromBlocks, stripCommentMarks } from "./doc-blocks";

export const DOCUMENT_KINDS = ["material", "meeting", "memo"] as const;

export const DOCUMENT_KIND_LABEL: Record<DocumentKind, string> = {
  material: "문서",
  meeting: "회의록",
  memo: "메모",
};

export const DOCUMENT_VISIBILITY_LABEL = {
  private: "비공개",
  plan: "공개",
} as const;

/** 로컬 JSON이 1차 저장이다. 클라우드 강제는 같은 함수에 mode를 넘겨 검증한다. */
export const DOCUMENT_HOST: "local" | "cloud" = "local";

export const TRASH_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

/** 실행 작업 제목에 별도 한계가 없어, 선택 평문은 한 줄로 접고 500자에서 자른다. */
export const SELECTION_TITLE_MAX = 500;

export const MISSING_DOCUMENT_MESSAGE = "문서를 찾을 수 없습니다";

let writeFault = false;

const bodyDrafts = new Map<string, DocumentBlock[]>();

/** 저장이 거절된 본문. 라우트를 빼앗지 않고 메모리에 남긴다. */
export function rememberDocumentDraft(id: string, blocks: DocumentBlock[]) {
  bodyDrafts.set(id, blocks);
}

export function peekDocumentDraft(id: string): DocumentBlock[] | undefined {
  return bodyDrafts.get(id);
}

export function clearDocumentDraft(id: string) {
  bodyDrafts.delete(id);
}

/** 테스트가 저장 실패를 흉내 낸다. 화면은 본문을 메모리에 남긴다. */
export function setDocumentWriteFault(next: boolean) {
  writeFault = next;
}

export function documentWriteBlocked(): boolean {
  return writeFault;
}

export function documentCode(seq: number): string {
  return `DOC-${seq}`;
}

export function documentTitle(title: string): string {
  const text = title.trim();
  return text || "제목 없음";
}

export function nextDocumentSeq(documents: readonly Pick<PlanDocument, "seq">[]): number {
  let max = 0;
  for (const doc of documents) if (doc.seq > max) max = doc.seq;
  return max + 1;
}

export function selectionTitle(text: string): string | null {
  const one = text.replace(/\s+/g, " ").trim();
  if (!one) return null;
  return one.length > SELECTION_TITLE_MAX ? one.slice(0, SELECTION_TITLE_MAX) : one;
}

export function isBlankDocument(doc: Pick<PlanDocument, "title" | "plainText" | "attachments" | "blocks">): boolean {
  if (doc.title.trim()) return false;
  if (doc.plainText.trim()) return false;
  if (doc.attachments.length > 0) return false;
  return isBlankBlocks(doc.blocks);
}

export function emptyBlocks(): DocumentBlock[] {
  return [{ type: "paragraph", content: [] }];
}

export function makeDocument(
  partial: Partial<PlanDocument> & { authorId: string; seq: number },
  now = new Date().toISOString(),
): PlanDocument {
  const blocks = partial.blocks ?? emptyBlocks();
  return {
    id: partial.id ?? newId("doc"),
    seq: partial.seq,
    title: partial.title ?? "",
    kind: partial.kind ?? "memo",
    visibility: partial.visibility ?? "private",
    authorId: partial.authorId,
    blocks,
    plainText: partial.plainText ?? plainTextFromBlocks(blocks),
    attachments: partial.attachments ?? [],
    comments: partial.comments ?? [],
    activity: partial.activity ?? [activity("문서를 만들었습니다", partial.authorId, now)],
    createdAt: partial.createdAt ?? now,
    updatedAt: partial.updatedAt ?? now,
    ...(partial.deletedAt ? { deletedAt: partial.deletedAt } : {}),
    ...(partial.heldAt ? { heldAt: partial.heldAt } : {}),
  };
}

export function activity(message: string, actor: string, now: string): TaskActivity {
  return { id: newId("ac"), at: now, actor, message };
}

export function withActivity(doc: PlanDocument, message: string, actor: string, now: string): PlanDocument {
  return { ...doc, activity: [...doc.activity, activity(message, actor, now)], updatedAt: now };
}

/**
 * 본문 키 입력은 실행 취소 스택을 늘리지 않는다.
 * 제목·연결·휴지통 같은 메타 변경만 한 단계를 쌓는다.
 */
export function nextHistory<T>(past: readonly T[], kind: "body" | "meta", current: T): T[] {
  if (kind === "body") return [...past];
  return [...past, current];
}

/** 실행 취소를 눌러도 그 사이 친 본문은 스냅샷의 옛 본문으로 되돌리지 않는다. */
export function keepDocumentBodies<T extends { documents: PlanDocument[] }>(snapshot: T, live: T): T {
  const liveDocs = new Map(live.documents.map((doc) => [doc.id, doc]));
  let changed = false;
  const documents = snapshot.documents.map((doc) => {
    const current = liveDocs.get(doc.id);
    if (!current || (current.blocks === doc.blocks && current.plainText === doc.plainText)) return doc;
    changed = true;
    return { ...doc, blocks: current.blocks, plainText: current.plainText };
  });
  return changed ? { ...snapshot, documents } : snapshot;
}

export function purgeExpiredDocuments<T extends Pick<Project, "documents" | "documentLinks">>(project: T, now = Date.now()): T {
  const expired = new Set(
    project.documents.filter((doc) => doc.deletedAt && now - Date.parse(doc.deletedAt) >= TRASH_RETENTION_MS).map((doc) => doc.id),
  );
  if (!expired.size) return project;
  return {
    ...project,
    documents: project.documents.filter((doc) => !expired.has(doc.id)),
    documentLinks: project.documentLinks.filter((link) => !expired.has(link.documentId)),
  };
}

const KINDS = new Set<string>(DOCUMENT_KINDS);

export function normalizePlanDocuments(project: Project, now = Date.now()): Pick<Project, "documents" | "documentLinks"> {
  const rawDocs = Array.isArray(project.documents) ? project.documents : [];
  const seen = new Set<string>();
  const documents: PlanDocument[] = [];
  for (const raw of rawDocs) {
    const doc = coerceDocument(raw);
    if (!doc || seen.has(doc.id)) continue;
    seen.add(doc.id);
    documents.push(doc);
  }
  const used = new Set<number>();
  for (const doc of documents) {
    if (doc.seq > 0 && !used.has(doc.seq)) {
      used.add(doc.seq);
      continue;
    }
    let seq = 1;
    while (used.has(seq)) seq += 1;
    used.add(seq);
    doc.seq = seq;
  }
  const rawLinks = Array.isArray(project.documentLinks) ? project.documentLinks : [];
  const linkSeen = new Set<string>();
  const documentLinks = [];
  const executionIds = new Set((project.executionTasks ?? []).map((task) => task.id));
  const taskIds = new Set(project.tasks.map((task) => task.id));
  const recordIds = new Set((project.records ?? []).map((record) => record.id));
  const stamped = new Date(now).toISOString();
  for (const raw of rawLinks) {
    const link = coerceLink(raw, documents);
    if (!link) continue;
    const key = `${link.documentId}:${link.targetKind}:${link.targetId}`;
    if (linkSeen.has(key)) continue;
    linkSeen.add(key);
    const alive =
      link.targetKind === "executionTask" ? executionIds.has(link.targetId) : link.targetKind === "scheduleTask" ? taskIds.has(link.targetId) : recordIds.has(link.targetId);
    if (!alive && !link.missingAt) link.missingAt = stamped;
    documentLinks.push(link);
  }
  return purgeExpiredDocuments({ documents, documentLinks }, now);
}

function coerceDocument(raw: unknown): PlanDocument | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<PlanDocument>;
  if (typeof row.id !== "string" || !row.id) return null;
  const now = new Date().toISOString();
  const kind = typeof row.kind === "string" && KINDS.has(row.kind) ? row.kind : "memo";
  const visibility = row.visibility === "plan" ? "plan" : "private";
  const blocks = stripCommentMarks(Array.isArray(row.blocks) ? (row.blocks as DocumentBlock[]) : emptyBlocks());
  return {
    id: row.id,
    seq: typeof row.seq === "number" && row.seq > 0 ? Math.floor(row.seq) : 0,
    title: typeof row.title === "string" ? row.title : "",
    kind,
    visibility,
    authorId: typeof row.authorId === "string" && row.authorId ? row.authorId : "",
    blocks,
    plainText: typeof row.plainText === "string" ? row.plainText : plainTextFromBlocks(blocks),
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
    comments: Array.isArray(row.comments) ? row.comments : [],
    activity: Array.isArray(row.activity) ? row.activity : [],
    createdAt: typeof row.createdAt === "string" ? row.createdAt : now,
    updatedAt: typeof row.updatedAt === "string" ? row.updatedAt : now,
    ...(typeof row.deletedAt === "string" ? { deletedAt: row.deletedAt } : {}),
    ...(typeof row.heldAt === "string" && row.heldAt ? { heldAt: row.heldAt } : {}),
  };
}

function coerceLink(raw: unknown, documents: readonly PlanDocument[]) {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<import("@/engine/types").DocumentLink>;
  if (typeof row.id !== "string" || !row.id) return null;
  if (typeof row.documentId !== "string" || !documents.some((doc) => doc.id === row.documentId)) return null;
  if (row.targetKind !== "executionTask" && row.targetKind !== "scheduleTask" && row.targetKind !== "record") return null;
  if (typeof row.targetId !== "string" || !row.targetId) return null;
  const origins = (Array.isArray(row.origins) ? row.origins : []).filter((item) => item === "explicit" || item === "mention");
  if (!origins.length) return null;
  const snapshot = row.snapshot && typeof row.snapshot === "object" ? row.snapshot : { title: "" };
  return {
    id: row.id,
    documentId: row.documentId,
    targetKind: row.targetKind,
    targetId: row.targetId,
    origins,
    snapshot: {
      ...(typeof snapshot.code === "string" ? { code: snapshot.code } : {}),
      title: typeof snapshot.title === "string" ? snapshot.title : "",
    },
    createdAt: typeof row.createdAt === "string" ? row.createdAt : new Date().toISOString(),
    createdBy: typeof row.createdBy === "string" ? row.createdBy : "",
    ...(typeof row.missingAt === "string" ? { missingAt: row.missingAt } : {}),
    ...(typeof row.blockId === "string" && row.blockId ? { blockId: row.blockId } : {}),
  };
}

export type DocSection = "all" | DocumentKind;

const DOC_SECTIONS = new Set<string>(DOCUMENT_KINDS);

/** 목록의 상위 탭. `view`가 종류면 그 값이고, 없으면 예전 `kind`를 읽는다. */
export function parseDocSection(view: string | null, legacyKind: string | null = null): DocSection {
  if (view && DOC_SECTIONS.has(view)) return view as DocumentKind;
  if (legacyKind && DOC_SECTIONS.has(legacyKind)) return legacyKind as DocumentKind;
  return "all";
}

/** 문서 화면의 규모. 탭과 범위를 바꿔도 같다. 비공개가 없으면 그 말은 뺀다. 워크스페이스는 플랜이 둘 이상일 때만 플랜 수를 붙인다. */
export function documentLibraryScale(docs: readonly Pick<PlanDocument, "visibility">[], scope?: { plans?: number }): string {
  let privateCount = 0;
  for (const doc of docs) if (doc.visibility === "private") privateCount += 1;
  const parts = [`${docs.length}개`];
  if (privateCount > 0) parts.push(`비공개 ${privateCount}`);
  if (scope?.plans && scope.plans > 1) parts.push(`${scope.plans}개 플랜`);
  return parts.join(" · ");
}

export type DocLinkFilter = "all" | "none";

/** 목록의 연결 필터. 없는 값과 빈 값은 전체다. */
export function parseDocLinkFilter(value: string | null): DocLinkFilter {
  return value === "none" ? "none" : "all";
}

export type DocSortKey = "updated" | "title" | "seq";

export function sortDocuments(list: readonly PlanDocument[], key: DocSortKey): PlanDocument[] {
  const next = [...list];
  next.sort((a, b) => {
    if (key === "title") return documentTitle(a.title).localeCompare(documentTitle(b.title), "ko") || b.seq - a.seq;
    if (key === "seq") return a.seq - b.seq;
    return a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : b.seq - a.seq;
  });
  return next;
}

export function documentMatchesQuery(doc: Pick<PlanDocument, "seq" | "title" | "plainText">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = `${documentCode(doc.seq)} ${doc.title} ${doc.plainText.slice(0, 2000)}`.toLowerCase();
  return hay.includes(q);
}

export function paletteDocuments(
  documents: readonly PlanDocument[],
  query: string,
  readable: (doc: PlanDocument) => boolean,
): PlanDocument[] {
  const live = documents.filter((doc) => !doc.deletedAt && readable(doc));
  const q = query.trim().toLowerCase();
  const matched = q ? live.filter((doc) => documentMatchesQuery(doc, q)) : sortDocuments(live, "updated");
  return matched.slice(0, 8);
}
